import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { adminApi } from "../../api/admin.api.js";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { SlugField } from "../../components/forms/SlugField.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { GalleryUpload } from "../../components/upload/GalleryUpload.jsx";

const DEFAULTS = {
  estateName: "",
  slug: "",
  suburb: "",
  direction: "north",
  lotSize: "",
  price: "",
  linkedDesign: "",
  inclusions: "",
  images: [],
  coordinates: { lat: "", lng: "" },
  status: "available",
  description: "",
  sortOrder: 0,
};

function buildPayload(values) {
  const out = { ...values };
  if (!out.slug) delete out.slug;
  if (typeof out.inclusions === "string") {
    out.inclusions = out.inclusions
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (out.price !== "" && out.price !== null) out.price = Number(out.price);
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  out.coordinates = {
    lat:
      out.coordinates?.lat === "" || out.coordinates?.lat == null
        ? null
        : Number(out.coordinates.lat),
    lng:
      out.coordinates?.lng === "" || out.coordinates?.lng == null
        ? null
        : Number(out.coordinates.lng),
  };
  return out;
}

export default function HouseLandFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("house-land", { toastLabel: "package" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const designsQuery = useQuery({
    queryKey: ["designs", "list-for-house-land"],
    queryFn: () => adminApi.list("designs", { limit: 100, status: "published" }),
    staleTime: 5 * 60_000,
  });

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) {
      reset({
        ...DEFAULTS,
        ...existing,
        linkedDesign:
          typeof existing.linkedDesign === "object"
            ? existing.linkedDesign?._id ?? ""
            : existing.linkedDesign ?? "",
        inclusions: Array.isArray(existing.inclusions)
          ? existing.inclusions.join("\n")
          : existing.inclusions ?? "",
        coordinates: {
          lat: existing.coordinates?.lat ?? "",
          lng: existing.coordinates?.lng ?? "",
        },
        images: existing.images ?? [],
      });
    }
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New House & Land Package · Admin" : "Edit Package · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/house-land/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  const watchedName = watch("estateName");
  const watchedSlug = watch("slug");

  return (
    <FormShell
      title={isNew ? "New House & Land Package" : `Edit "${existing?.estateName ?? ""}"`}
      backTo="/house-land"
      backLabel="Back to packages"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create package" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/house-land")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Visibility">
            <Select label="Status" {...register("status")}>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold</option>
            </Select>
            <Input type="number" label="Sort order" {...register("sortOrder")} />
          </FormSection>

          <FormSection title="Linked design">
            <Select
              label="Featured design"
              required
              error={errors.linkedDesign?.message}
              {...register("linkedDesign", { required: "Pick a linked design" })}
            >
              <option value="">Select a design</option>
              {designsQuery.data?.items?.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </FormSection>

          <FormSection title="Location pin">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Latitude"
                type="number"
                step="0.000001"
                {...register("coordinates.lat")}
              />
              <Input
                label="Longitude"
                type="number"
                step="0.000001"
                {...register("coordinates.lng")}
              />
            </div>
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Estate name"
          required
          error={errors.estateName?.message}
          {...register("estateName", { required: "Estate name is required" })}
        />
        <SlugField
          value={watchedSlug}
          onChange={(v) => setValue("slug", v)}
          source={watchedName}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="Suburb"
            required
            error={errors.suburb?.message}
            {...register("suburb", { required: "Suburb is required" })}
          />
          <Select
            label="Direction"
            required
            error={errors.direction?.message}
            {...register("direction", { required: "Direction is required" })}
          >
            <option value="north">North</option>
            <option value="south_east">South-East</option>
            <option value="west">West</option>
          </Select>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input
            label="Lot size"
            placeholder="e.g. 350m²"
            {...register("lotSize")}
          />
          <Input
            label="Price (AUD)"
            type="number"
            min="0"
            required
            error={errors.price?.message}
            {...register("price", {
              required: "Price is required",
              valueAsNumber: false,
            })}
          />
        </div>
        <Textarea
          label="Description"
          rows={3}
          {...register("description")}
        />
      </FormSection>

      <FormSection
        title="Inclusions"
        description="One per line — joined into a list on the public card."
      >
        <Textarea label="Inclusions" rows={5} {...register("inclusions")} />
      </FormSection>

      <FormSection title="Photos" description="First image is used as the listing card hero.">
        <Controller
          control={control}
          name="images"
          render={({ field }) => (
            <GalleryUpload
              value={field.value ?? []}
              onChange={field.onChange}
              subdir="house-land"
              hint="Drag to reorder. 6–10 photos recommended."
            />
          )}
        />
      </FormSection>
    </FormShell>
  );
}
